import React, { useRef, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { profileBelongsToUser } from '../../contexts/sessionIdentity';

/**
 * Gate incomplete profiles to /complete-profile.
 *
 * Critical: once currentUser exists, NEVER replace the route tree with a
 * full-viewport spinner. Sidebar paints from currentUser; blanking main here
 * was the sitewide "sidebar-only" bug (also left a 5s band-aid spinner).
 */
const ProfileCompletionGuard = ({ children }) => {
  const {
    currentUser,
    userProfile,
    isProfileComplete,
    profileCheckLoading,
    isLoading,
    authInitialized,
  } = useAuth();
  const location = useLocation();
  const lastLogRef = useRef(null);

  useEffect(() => {
    const currentState = JSON.stringify({
      currentUser: !!currentUser,
      isProfileComplete,
      profileCheckLoading,
      isLoading,
      authInitialized,
      pathname: location.pathname,
    });

    if (lastLogRef.current !== currentState) {
      lastLogRef.current = currentState;
    }
  }, [
    currentUser,
    isProfileComplete,
    profileCheckLoading,
    isLoading,
    authInitialized,
    location.pathname,
  ]);

  const allowedRoutes = [
    '/complete-profile',
    '/welcome',
    '/logout',
    '/login',
    '/register',
  ];

  // Cold start only — no identity yet
  if (isLoading && !currentUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: '#0C0D0F',
        }}
      >
        <CircularProgress sx={{ color: '#D4AF37' }} />
      </Box>
    );
  }

  if (!currentUser) {
    return children;
  }

  // Logged in: always paint routes. Redirect only after profile I/O settles.
  if (!authInitialized || profileCheckLoading) {
    return children;
  }

  // MKT-37 / VAULT-37: never send a settled-looking session to the wizard
  // while the leftover profile belongs to a different uid (session swap).
  if (
    !isProfileComplete &&
    !allowedRoutes.includes(location.pathname) &&
    (!userProfile || profileBelongsToUser(userProfile, currentUser.uid))
  ) {
    return <Navigate to="/complete-profile" replace />;
  }

  if (isProfileComplete && location.pathname === '/complete-profile') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProfileCompletionGuard;
